import { useEffect } from "react";
import { devWarn } from "@/lib/devLogger";

const STORAGE_KEY = "gg-soundscape-pref";

// Sessions-only preference store for the ambient soundscape (play/mute/
// volume + whether the visitor has opted in at all). This is NOT sensitive
// data -- no personal or authentication information is stored here, only a
// UI preference -- so plain sessionStorage is an acceptable, low-risk choice
// (cleared automatically when the browser tab closes, never sent to a
// server, and not usable for session hijacking).
const readStoredPreference = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
  } catch (error) {
    devWarn("Soundscape preference could not be read from sessionStorage:", error);
    return null;
  }
};

const writeStoredPreference = (pref) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pref));
  } catch (error) {
    devWarn("Soundscape preference could not be persisted to sessionStorage:", error);
  }
};

/**
 * Owns ONLY sessionStorage persistence for the soundscape preference --
 * restoring it once on mount, and re-persisting whenever any of the
 * tracked values change. Split out of useSoundscape.js so playback
 * control logic (useAudioControls) and preference persistence are each
 * independently readable/testable, per the "split into multiple custom
 * hooks" recommendation.
 */
export const useAudioPreferences = ({ hasEntered, playing, muted, volume, setHasEntered, setPlaying, setMuted, setVolume }) => {
  // Restore any preference saved earlier in this browser session.
  useEffect(() => {
    const saved = readStoredPreference();
    if (saved) {
      setHasEntered(!!saved.entered);
      setPlaying(!!saved.playing);
      setMuted(!!saved.muted);
      if (typeof saved.volume === "number") setVolume(saved.volume);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    writeStoredPreference({ entered: hasEntered, playing, muted, volume });
  }, [hasEntered, playing, muted, volume]);
};
